<template>
  <div
    class="bg-gray-lighter flex flex-col md:flex-row relative"
    :style="{ marginBottom: `${footerHeight}px` }"
  >
    <div
      :id="SCROLLABLE_CONTAINER_ID"
      class="border-gray-light overflow-auto md:pb-10 px-4 md:pl-12"
      :style="!mobileView && { ...style, width: leftSideWidth }"
    >
      <div v-if="leftLoading" class="spinner-area">
        <icon-spinner
          class="spinner stroke-primary"
          width="64px"
          height="64px"
        />
      </div>
      <slot v-else name="leftSide" />
    </div>
    <div
      v-if="!mobileView && resizable"
      class="resize-handle"
      @mousedown="startResize"
    ></div>
    <div
      class="border-t md:border-t-0 md:border-l border-gray-light overflow-auto"
      :class="rightFluid ? '' : 'md:pb-10 md:pr-12'"
      :style="!mobileView && { ...style, width: rightSideWidth }"
    >
      <div v-if="rightLoading" class="spinner-area">
        <icon-spinner
          class="spinner stroke-primary"
          width="64px"
          height="64px"
        />
      </div>
      <slot v-else name="rightSide" />
    </div>
    <slot></slot>
    <footer-menu v-if="footer" ref="footer">
      <slot name="footer"></slot>
    </footer-menu>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import IconSpinner from '@/components/Icon/icons/IconSpinner.vue';
import FooterMenu from '@/components/Footer/FooterMenu';
import { SCROLLABLE_CONTAINER_ID } from '@/layouts/constants';
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api';

export default defineComponent({
  components: {
    FooterMenu,
    IconSpinner,
  },
  props: {
    leftLoading: {
      type: Boolean,
      default: false,
    },
    rightLoading: {
      type: Boolean,
      default: false,
    },
    leftWidth: {
      type: Number,
      default: 50,
    },
    rightWidth: {
      type: Number,
      default: 50,
    },
    footer: {
      type: Boolean,
      default: false,
    },
    rightFluid: {
      type: Boolean,
      default: false,
    },
    resizable: {
      type: Boolean,
      default: false,
    },
    resizableId: {
      type: String,
      default: 'split-layout',
    },
  },
  setup(props) {
    const isResizing = ref(false);
    const currentLeftWidth = ref(props.leftWidth);

    const loadSavedWidth = () => {
      try {
        const saved = localStorage.getItem(`split-layout-${props.resizableId}`);
        if (saved) {
          currentLeftWidth.value = parseInt(saved, 10);
        }
      } catch (e) {
        console.error(e);
      }
    };

    const saveWidth = (width) => {
      try {
        localStorage.setItem(
          `split-layout-${props.resizableId}`,
          width.toString(),
        );
      } catch (e) {
        console.error(e);
      }
    };

    const leftSideWidth = computed(() => {
      if (props.resizable) {
        return `${currentLeftWidth.value}%`;
      }
      return `${props.leftWidth}%`;
    });

    const rightSideWidth = computed(() => {
      if (props.resizable) {
        return `${100 - currentLeftWidth.value}%`;
      }
      return `${props.rightWidth}%`;
    });

    const startResize = (e) => {
      e.preventDefault();
      isResizing.value = true;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };

    const handleResize = (e) => {
      if (!isResizing.value || !props.resizable) return;

      const containerWidth = window.innerWidth;
      const newLeftWidth = (e.clientX / containerWidth) * 100;

      currentLeftWidth.value = Math.max(35, Math.min(80, newLeftWidth));
    };

    const stopResize = () => {
      if (isResizing.value) {
        isResizing.value = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        saveWidth(currentLeftWidth.value);
      }
    };

    onMounted(() => {
      if (props.resizable) {
        loadSavedWidth();
        document.addEventListener('mousemove', handleResize);
        document.addEventListener('mouseup', stopResize);
      }
    });

    onBeforeUnmount(() => {
      if (props.resizable) {
        document.removeEventListener('mousemove', handleResize);
        document.removeEventListener('mouseup', stopResize);
        stopResize();
      }
    });

    return {
      currentLeftWidth,
      leftSideWidth,
      rightSideWidth,
      startResize,
    };
  },
  data() {
    return {
      footerHeight: 0,
      SCROLLABLE_CONTAINER_ID,
    };
  },
  computed: {
    ...mapState('ui', {
      mobileView: (state) => state.mobileView.active,
      offsets: 'offsets',
    }),
    navbarHeight() {
      if (this.$route.matched.find(({ name }) => name === 'Brand')) {
        return this.offsets.navBarBrand;
      }
      return this.offsets.navBar;
    },
    style() {
      return {
        height: `calc(100vh - ${this.footerHeight + this.navbarHeight}px)`,
      };
    },
  },
  mounted() {
    if (this.footer) {
      const { height } = this.$refs.footer.$el.getBoundingClientRect();
      this.footerHeight = height;
    }
  },
});
</script>

<style lang="scss" scoped>
.spinner-area {
  @apply w-full h-full flex justify-center items-center;
}

.resize-handle {
  width: 6px;
  cursor: col-resize;
  background: transparent;
  border-left: 1px solid #dddddd;
  position: relative;
  flex-shrink: 0;

  &:hover {
    background: black;
  }

  &:active {
    background: black;
  }
}
</style>
